import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { ApiError } from '../api/client';
import {
  contestApi, contestError, MANAGED_CONFIRMATION, RESTART_CONFIRMATION,
  type ContestDetail, type ContestDnsMode, type ContestItem, type ContestOptions, type ContestRun,
  type ExistingContestDomain, type ManagedContestDomain,
} from './contestApi';
import './ForeignDns.css';
import './DomainContests.css';

const LABELS: Record<string, string> = {
  queued: 'Queued', running: 'Running', completed: 'Submitted', needs_attention: 'Needs attention',
  case_created: 'Case opened', cname_written: 'Proof record added', waiting_verify: 'Verifying DNS',
  submit_unconfirmed: 'Confirming submission', submitted: 'Submitted', needs_human: 'Needs attention', failed: 'Failed',
  awaiting_record: 'Add DNS record', moved: 'Moved to a new run',
};

const DNS_MODES: { value: ContestDnsMode; label: string; blurb: string }[] = [
  { value: 'cloudflare', label: 'Add records in our Cloudflare', blurb: 'Records are added automatically. Any domain not found in our Cloudflare accounts switches to a manual record.' },
  { value: 'manual', label: 'Our team adds the records', blurb: 'For domains at a customer registrar or in a Cloudflare account we do not hold. The run lists every record to copy.' },
];

function isAttention(item: ContestItem): boolean {
  return !item.released_at && (item.status === 'failed' || item.status === 'needs_human');
}

/** A manual case whose CNAME the team still has to add (or fix). */
function isWaitingRecord(item: ContestItem): boolean {
  return item.dns_mode === 'manual' && !!item.case_id && !item.cname_at && item.status !== 'submitted' && !item.released_at;
}

function CopyButton({ text, label = 'Copy' }: { text: string; label?: string }): JSX.Element {
  const [state, setState] = useState<'idle' | 'copied' | 'failed'>('idle');
  useEffect(() => {
    if (state === 'idle') return;
    const timer = window.setTimeout(() => setState('idle'), 1500);
    return () => window.clearTimeout(timer);
  }, [state]);
  async function copy(): Promise<void> {
    try { await navigator.clipboard.writeText(text); setState('copied'); }
    catch { setState('failed'); }
  }
  return <button type="button" className="fd-btn fd-btn--ghost fd-btn--small contest-copy" onClick={() => void copy()}>
    {state === 'copied' ? 'Copied' : state === 'failed' ? 'Copy failed' : label}
  </button>;
}

function Badge({ status }: { status: string }): JSX.Element {
  const variant = status === 'completed' || status === 'submitted' ? 'ok'
    : status === 'failed' ? 'err'
      : ['needs_attention', 'needs_human', 'queued', 'awaiting_record'].includes(status) ? 'warn' : 'info';
  return <span className={`fd-badge fd-badge--${variant}`}>{LABELS[status] ?? status}</span>;
}

function date(value: string | null): string {
  return value ? new Date(value).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
}

function caseLink(value: string | null): string | null {
  if (!value) return null;
  try {
    const url = new URL(value);
    return url.origin === 'https://toolbox.googleapps.com' && /^\/apps\/recovery\/(ownership|domain_in_use)(\/|$)/.test(url.pathname) ? url.href : null;
  } catch { return null; }
}

export function DomainContests(): JSX.Element {
  const [runs, setRuns] = useState<ContestRun[]>([]);
  const [options, setOptions] = useState<ContestOptions | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [optionsError, setOptionsError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [label, setLabel] = useState('');
  const [domains, setDomains] = useState('');
  const [dnsMode, setDnsMode] = useState<ContestDnsMode>('cloudflare');
  const [creating, setCreating] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);
  const [managed, setManaged] = useState<ManagedContestDomain[] | null>(null);
  const [managedConfirm, setManagedConfirm] = useState('');
  const [existing, setExisting] = useState<ExistingContestDomain[] | null>(null);
  const [restartConfirm, setRestartConfirm] = useState('');
  const listSequence = useRef(0);

  const loadRuns = useCallback(async () => {
    const sequence = ++listSequence.current;
    try {
      const result = await contestApi.runs();
      if (sequence !== listSequence.current) return;
      setRuns(result.runs);
      setLoadError(null);
    } catch (err) {
      if (sequence === listSequence.current) setLoadError(contestError(err));
    } finally {
      if (sequence === listSequence.current) setLoaded(true);
    }
  }, []);

  const loadOptions = useCallback(async () => {
    try {
      const next = await contestApi.options();
      setOptions(next);
      setOptionsError(null);
    } catch (err) { setOptionsError(contestError(err)); }
  }, []);

  useEffect(() => { void loadRuns(); void loadOptions(); }, [loadRuns, loadOptions]);

  const active = runs.some((run) => run.status === 'queued' || run.status === 'running');
  useEffect(() => {
    if (selected || !active) return;
    const timer = window.setInterval(() => { void loadRuns(); }, 5000);
    return () => window.clearInterval(timer);
  }, [selected, active, loadRuns]);

  const entries = domains.split(/\r?\n/).map((domain) => domain.trim().toLowerCase()).filter(Boolean);
  const unique = [...new Set(entries)];
  const ready = options !== null && options.captcha_configured && options.contacts_configured
    && (dnsMode === 'manual' || options.cloudflare_configured);

  async function create(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (creating || !ready || !label.trim() || unique.length === 0 || unique.length > 500) return;
    if (managed && managedConfirm !== MANAGED_CONFIRMATION) return;
    if (existing && restartConfirm !== RESTART_CONFIRMATION) return;
    setCreating(true);
    setError(null);
    try {
      const run = await contestApi.create({
        label: label.trim(), domains: unique, dns_mode: dnsMode,
        ...(managed && { managed_confirmation: managedConfirm, accept_managed_domains: managed.map((row) => row.domain) }),
        ...(existing && { restart_confirmation: restartConfirm, restart_domains: existing.map((row) => row.domain) }),
      });
      setLabel('');
      setDomains('');
      setManaged(null);
      setManagedConfirm('');
      setExisting(null);
      setRestartConfirm('');
      setSelected(run.id);
      void loadRuns();
    } catch (err) {
      const data = err instanceof ApiError
        ? err.data as { managed?: ManagedContestDomain[]; existing?: ExistingContestDomain[] } | undefined : undefined;
      if (err instanceof ApiError && err.code === 'MANAGED_DOMAINS' && data?.managed?.length) { setManaged(data.managed); setManagedConfirm(''); }
      else if (err instanceof ApiError && err.code === 'EXISTING_CONTEST' && data?.existing?.length) { setExisting(data.existing); setRestartConfirm(''); }
      else setError(contestError(err));
    }
    finally { setCreating(false); }
  }

  if (selected) return <ContestRunDetail key={selected} id={selected} onBack={() => { setSelected(null); void loadRuns(); void loadOptions(); }} />;

  return (
    <div className="fd"><div className="fd-scroll">
      <header className="fd-header">
        <h1 className="fd-title">Domain Contests</h1>
        <p className="fd-sub">Open Google domain ownership cases, add the Cloudflare proof records, and submit each case. A run is done when every case is submitted. Google’s release decision happens later.</p>
      </header>
      <section className="fd-card">
        <div className="fd-card-header"><h2 className="fd-card-title">Start a run</h2></div>
        <form className="fd-card-body" onSubmit={(event) => void create(event)}>
          {error && <div className="fd-notice fd-notice--err" role="alert">{error}</div>}
          {optionsError && <div className="fd-notice fd-notice--err" role="alert">Could not load run settings: {optionsError} <button type="button" className="fd-btn fd-btn--secondary fd-btn--small" onClick={() => void loadOptions()}>Retry</button></div>}
          {!options && !optionsError && <div className="fd-hint">Loading run settings…</div>}
          {options && !ready && <div className="fd-notice fd-notice--warn">An admin needs to configure {[
            !options.captcha_configured && 'the 2Captcha API key',
            !options.contacts_configured && 'contact emails',
            dnsMode === 'cloudflare' && !options.cloudflare_configured && 'a Cloudflare account',
          ].filter(Boolean).join(', ')} in Settings before a run can start.</div>}
          <div className="contest-form-grid">
            <div className="fd-field">
              <label className="fd-field-label" htmlFor="contest-label">Run name</label>
              <input id="contest-label" className="fd-input" value={label} onChange={(event) => setLabel(event.target.value)} maxLength={120} required disabled={creating} placeholder="Customer or batch name" />
            </div>
            <div className="fd-field">
              <label className="fd-field-label" htmlFor="contest-domains">Domains</label>
              <textarea id="contest-domains" className="fd-textarea fd-mono" value={domains} onChange={(event) => { setDomains(event.target.value); setManaged(null); setManagedConfirm(''); setExisting(null); setRestartConfirm(''); }} disabled={creating} required spellCheck={false} placeholder={'example.com\nexample.org'} aria-describedby="contest-domains-hint" />
              <span className="fd-hint" id="contest-domains-hint">One domain per line, up to 500 per run. {unique.length.toLocaleString()} unique {unique.length === 1 ? 'domain' : 'domains'}{entries.length > unique.length ? ` · ${entries.length - unique.length} duplicate lines will be ignored` : ''}. {dnsMode === 'cloudflare' ? 'Active Cloudflare zones are found automatically across saved accounts. Only the case’s proof CNAME is added.' : 'Each case gets one CNAME for your team to add. The run page lists them all to copy.'}</span>
              {unique.length > 500 && <span className="contest-error" role="alert">Split these domains into batches of 500 or fewer.</span>}
            </div>
            <fieldset className="fd-field contest-modes" disabled={creating}>
              <legend className="fd-field-label">DNS proof records</legend>
              {DNS_MODES.map((mode) => <label key={mode.value} className={`contest-mode${dnsMode === mode.value ? ' contest-mode--on' : ''}`}>
                <input type="radio" name="contest-dns-mode" value={mode.value} checked={dnsMode === mode.value} onChange={() => setDnsMode(mode.value)} />
                <span className="contest-mode-text"><span className="contest-mode-label">{mode.label}</span><span className="contest-mode-blurb">{mode.blurb}</span></span>
              </label>)}
            </fieldset>
          </div>
          {managed && <div className="fd-notice fd-notice--err contest-danger" role="alert">
            <strong className="contest-danger-title">Warning: {managed.length} {managed.length === 1 ? 'domain is' : 'domains are'} on our own admin panels</strong>
            <p>If Google accepts a contest, it removes the domain from the panel that holds it. Only continue if that panel is lost to us, for example a suspended panel. Check every panel below before you confirm.</p>
            <ul className="contest-danger-list">{managed.map((row) => <li key={row.domain}><span className="fd-mono">{row.domain}</span> on {row.panels.join(', ')}</li>)}</ul>
            <label className="fd-field-label contest-danger-label" htmlFor="contest-managed-confirm">Type <span className="fd-mono">{MANAGED_CONFIRMATION}</span> to contest these domains</label>
            <input id="contest-managed-confirm" className="fd-input fd-mono contest-danger-input" value={managedConfirm} onChange={(event) => setManagedConfirm(event.target.value)} disabled={creating} autoComplete="off" spellCheck={false} placeholder={MANAGED_CONFIRMATION} />
          </div>}
          {existing && <div className="fd-notice fd-notice--err contest-danger" role="alert">
            <strong className="contest-danger-title">{existing.length} {existing.length === 1 ? 'domain is' : 'domains are'} still in an earlier contest run</strong>
            <p>Starting again takes each domain out of its earlier run and opens a new Google case for it. Domains already submitted keep their old case with Google as well. If an earlier run only needs DNS records added, open it and add them there instead.</p>
            <ul className="contest-danger-list">{existing.map((row) => <li key={row.domain}><span className="fd-mono">{row.domain}</span> - {LABELS[row.status] ?? row.status} in {row.run_name}{row.case_id ? ` (case ${row.case_id})` : ''}</li>)}</ul>
            <div className="fd-actions">{[...new Map(existing.map((row) => [row.run_id, row.run_name]))].map(([runId, runName]) => <button key={runId} type="button" className="fd-btn fd-btn--secondary fd-btn--small" onClick={() => setSelected(runId)}>Open {runName}</button>)}</div>
            <label className="fd-field-label contest-danger-label" htmlFor="contest-restart-confirm">Type <span className="fd-mono">{RESTART_CONFIRMATION}</span> to start these domains again</label>
            <input id="contest-restart-confirm" className="fd-input fd-mono contest-danger-input" value={restartConfirm} onChange={(event) => setRestartConfirm(event.target.value)} disabled={creating} autoComplete="off" spellCheck={false} placeholder={RESTART_CONFIRMATION} />
          </div>}
          <div className="fd-actions"><span className="fd-hint">Runs continue on worker droplets when you leave this page.</span><div className="fd-actions-spacer" /><button type="submit" className={`fd-btn ${managed || existing ? 'fd-btn--danger' : 'fd-btn--primary'}`} disabled={creating || !ready || !label.trim() || unique.length === 0 || unique.length > 500 || (managed !== null && managedConfirm !== MANAGED_CONFIRMATION) || (existing !== null && restartConfirm !== RESTART_CONFIRMATION)}>{creating ? 'Starting…' : existing ? 'Start again in a new run' : managed ? 'Contest domains on our panels' : 'Start contest run'}</button></div>
        </form>
      </section>
      {loadError && <div className="fd-toast fd-toast--error" role="alert">Could not refresh runs: {loadError}</div>}
      <section className="fd-card">
        <div className="fd-card-header"><h2 className="fd-card-title">Run history</h2><button type="button" className="fd-btn fd-btn--ghost fd-btn--small" onClick={() => void loadRuns()}>Refresh</button></div>
        <div className="fd-table-wrap"><table className="fd-table">
          <thead><tr><th>Run</th><th>Status</th><th>Submitted</th><th>Needs attention</th><th>Created</th><th><span className="fd-hint">Details</span></th></tr></thead>
          <tbody>{runs.length === 0 ? <tr><td colSpan={6} className="fd-empty">{!loaded ? 'Loading runs…' : loadError ? 'Run history is unavailable. Refresh to try again.' : 'No contest runs yet.'}</td></tr> : runs.map((run) => <tr key={run.id}>
            <td><strong>{run.label}</strong><span className="contest-caption">{run.created_by}</span>{!!run.records_waiting_count && <span className="contest-caption contest-waiting">{run.records_waiting_count} DNS {run.records_waiting_count === 1 ? 'record' : 'records'} to add</span>}</td><td><Badge status={run.status} /></td>
            <td>{run.submitted_count} / {run.total_count}</td><td>{run.attention_count || '—'}</td><td className="fd-cell-faint">{date(run.created_at)}</td>
            <td><button type="button" className="fd-btn fd-btn--secondary fd-btn--small" onClick={() => setSelected(run.id)} aria-label={`Open ${run.label}`}>Open</button></td>
          </tr>)}</tbody>
        </table></div>
      </section>
    </div></div>
  );
}

function ContestRunDetail({ id, onBack }: { id: string; onBack: () => void }): JSX.Element {
  const [detail, setDetail] = useState<ContestDetail | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [retrying, setRetrying] = useState(false);
  const [attentionOnly, setAttentionOnly] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const sequence = useRef(0);

  const reload = useCallback(async () => {
    const request = ++sequence.current;
    try {
      const next = await contestApi.detail(id);
      if (request !== sequence.current) return;
      setDetail(next);
      setLoadError(null);
    } catch (err) { if (request === sequence.current) setLoadError(contestError(err)); }
  }, [id]);

  useEffect(() => { void reload(); return () => { sequence.current += 1; }; }, [reload]);
  const active = detail?.run.status === 'queued' || detail?.run.status === 'running';
  useEffect(() => {
    if (!active) return;
    const timer = window.setInterval(() => { void reload(); }, 5000);
    return () => window.clearInterval(timer);
  }, [active, reload]);

  async function retry(): Promise<void> {
    if (retrying) return;
    setRetrying(true);
    setActionError(null);
    setNotice(null);
    try {
      await contestApi.retry(id);
      setNotice('Attention items queued for another attempt. Submitted cases are preserved.');
      await reload();
    } catch (err) { setActionError(contestError(err)); }
    finally { setRetrying(false); }
  }

  const items = detail?.items.filter((item) => !attentionOnly || isAttention(item)) ?? [];
  const records = detail?.items.filter(isWaitingRecord) ?? [];
  const opening = detail?.items.filter((item) => item.dns_mode === 'manual' && !item.case_id && !isAttention(item) && !item.released_at).length ?? 0;
  const retryable = detail?.items.some((item) => isAttention(item) && !(item.case_creation_inflight && !item.case_id)) ?? false;
  return (
    <div className="fd"><div className="fd-scroll">
      <div className="fd-runheader"><button type="button" className="fd-btn fd-btn--ghost fd-btn--small" onClick={onBack}>Back</button><h1 className="fd-title contest-title">{detail?.run.label ?? 'Contest run'}</h1>{detail && <Badge status={detail.run.status} />}</div>
      {loadError && <div className="fd-toast fd-toast--error" role="alert">Could not refresh this run: {loadError} <button type="button" className="fd-btn fd-btn--secondary fd-btn--small" onClick={() => void reload()}>Refresh</button></div>}
      {actionError && <div className="fd-toast fd-toast--error" role="alert">{actionError}</div>}
      {notice && <div className="fd-toast fd-toast--info" role="status">{notice}</div>}
      {!detail ? <section className="fd-card"><div className="fd-empty">{loadError ? 'Run details are unavailable.' : 'Loading run…'}</div></section> : <>
        <div className="fd-runmeta"><span><strong>Created</strong>{date(detail.run.created_at)} by {detail.run.created_by}</span>{detail.run.finished_at && <span><strong>Finished</strong>{date(detail.run.finished_at)}</span>}</div>
        <section className="fd-card"><div className="fd-card-body">
          <div className="contest-summary">
            <div className="contest-stat"><strong>{detail.run.total_count}</strong>Domains</div>
            <div className="contest-stat contest-stat--submitted"><strong>{detail.run.submitted_count}</strong>Submitted</div>
            <div className="contest-stat"><strong>{detail.run.active_count}</strong>In progress</div>
            <div className="contest-stat contest-stat--attention"><strong>{detail.run.attention_count}</strong>Need attention</div>
          </div>
          <progress className="contest-progress" value={detail.run.submitted_count} max={Math.max(1, detail.run.total_count)} aria-label="Cases submitted" />
          <p className="fd-hint">{detail.run.status === 'completed' ? 'All cases have been submitted. This run is complete.' : 'Completion means every case has been submitted.'} Google’s release decision happens later.</p>
          {detail.run.attention_count > 0 && <div className="fd-notice fd-notice--warn">{detail.run.attention_count} {detail.run.attention_count === 1 ? 'case needs' : 'cases need'} attention. Review the per-domain error below and follow its next step before retrying.</div>}
        </div></section>
        {(records.length > 0 || opening > 0) && <ManualRecords records={records} opening={opening} />}
        <section className="fd-card">
          <div className="fd-card-header"><h2 className="fd-card-title">Cases</h2><div className="fd-actions">
            <label className="contest-filter"><input type="checkbox" checked={attentionOnly} onChange={(event) => setAttentionOnly(event.target.checked)} />Needs attention only</label>
            <button type="button" className="fd-btn fd-btn--ghost fd-btn--small" onClick={() => void reload()}>Refresh</button>
            {retryable && <button type="button" className="fd-btn fd-btn--primary fd-btn--small" disabled={retrying} onClick={() => void retry()}>{retrying ? 'Retrying…' : 'Retry attention items'}</button>}
          </div></div>
          <div className="fd-table-wrap"><table className="fd-table">
            <thead><tr><th>Domain / case</th><th>Stage</th><th>Attempts</th><th>Last update / result</th></tr></thead>
            <tbody>{items.length === 0 ? <tr><td className="fd-empty" colSpan={4}>{attentionOnly ? 'No cases need attention.' : 'No cases in this run.'}</td></tr> : items.map((item) => {
              const link = caseLink(item.ownership_url);
              return <tr key={item.id}>
                <td><span className="fd-cell-mono contest-domain">{item.domain}</span>{link ? <span className="contest-caption"><a className="contest-link" href={link} target="_blank" rel="noopener noreferrer">{item.case_id ? `Case ${item.case_id}` : 'Open case'} ↗</a></span> : item.case_id && <span className="contest-caption">Case {item.case_id}</span>}</td>
                <td><Badge status={item.released_at ? 'moved' : isWaitingRecord(item) && item.status === 'case_created' ? 'awaiting_record' : item.status} />{item.worker_id && <span className="contest-caption">{item.worker_id}</span>}</td>
                <td><span className="contest-caption">Open: {item.attempts_create}</span><span className="contest-caption">Submit: {item.attempts_submit}</span></td>
                <td><span className="contest-caption">{date(item.submitted_at ?? item.updated_at)}</span>{item.released_at ? <span className="fd-hint">Moved to a newer contest run</span> : item.status === 'submitted' ? <span className="fd-hint">Submission confirmed</span> : item.last_error ? <p className="contest-error">{item.last_error}</p> : <span className="fd-hint">{item.status === 'queued' ? 'Waiting for a worker' : isWaitingRecord(item) ? 'Waiting for the DNS record' : 'Processing'}</span>}
                  {isAttention(item) && !item.case_id && <RecoverCase runId={id} item={item} onRecovered={reload} />}
                </td>
              </tr>;
            })}</tbody>
          </table></div>
        </section>
      </>}
    </div></div>
  );
}

function ManualRecords({ records, opening }: { records: ContestItem[]; opening: number }): JSX.Element {
  const rows = records.map((item) => ({ item, host: item.case_id!, name: `${item.case_id}.${item.domain}` }));
  const table = ['Domain\tType\tHost\tFull name\tValue',
    ...rows.map((row) => `${row.item.domain}\tCNAME\t${row.host}\t${row.name}\tgoogle.com`)].join('\n');
  const text = rows.map((row) => `${row.name} CNAME google.com`).join('\n');
  return <section className="fd-card">
    <div className="fd-card-header"><h2 className="fd-card-title">DNS records to add ({records.length})</h2>{rows.length > 0 && <div className="fd-actions">
      <CopyButton text={table} label="Copy all (sheet)" />
      <CopyButton text={text} label="Copy all (text)" />
    </div>}</div>
    <div className="fd-card-body">
      <p className="fd-hint">Add each CNAME where the domain's DNS is hosted. Most DNS hosts add the domain for you, so use the Host value; if yours wants the full name, use that instead. In Cloudflare, turn the proxy off. Records are checked every 2 minutes and each case is submitted once its record is live.{opening > 0 && ` ${opening} more ${opening === 1 ? 'record appears' : 'records appear'} here as their cases open.`}</p>
    </div>
    {rows.length > 0 && <div className="fd-table-wrap"><table className="fd-table">
      <thead><tr><th>Domain</th><th>Type</th><th>Host</th><th>Value</th><th>Status</th></tr></thead>
      <tbody>{rows.map(({ item, host, name }) => <tr key={item.id}>
        <td className="fd-cell-mono contest-domain">{item.domain}</td>
        <td className="fd-cell-mono">CNAME</td>
        <td><span className="contest-record"><span className="fd-cell-mono">{host}</span><CopyButton text={host} /></span><span className="contest-record contest-caption"><span className="fd-mono">{name}</span><CopyButton text={name} label="Copy full" /></span></td>
        <td><span className="contest-record"><span className="fd-cell-mono">google.com</span><CopyButton text="google.com" /></span></td>
        <td>{item.last_error ? <p className="contest-error">{item.last_error}</p> : <span className="fd-hint">Waiting for the record</span>}</td>
      </tr>)}</tbody>
    </table></div>}
  </section>;
}

function RecoverCase({ runId, item, onRecovered }: { runId: string; item: ContestItem; onRecovered: () => Promise<void> }): JSX.Element {
  const [url, setUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function recover(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (saving) return;
    setSaving(true);
    setError(null);
    try {
      await contestApi.recoverCase(runId, item.id, url.trim());
      await onRecovered();
    } catch (err) { setError(contestError(err)); }
    finally { setSaving(false); }
  }
  return <form className="contest-recover" onSubmit={(event) => void recover(event)}>
    <span className="fd-hint">{item.case_creation_inflight ? 'Google may already have opened this case. Paste its ownership link to resume that case.' : 'If this case was opened manually, paste its ownership link to continue.'}</span>
    <label className="fd-field-label" htmlFor={`contest-case-${item.id}`}>Paste existing ownership link</label>
    <input id={`contest-case-${item.id}`} type="url" className="fd-input" value={url} onChange={(event) => setUrl(event.target.value)} required disabled={saving} placeholder="https://toolbox.googleapps.com/…" />
    {error && <div className="contest-error" role="alert">{error}</div>}
    <button type="submit" className="fd-btn fd-btn--secondary fd-btn--small" disabled={saving || !url.trim()}>{saving ? 'Resuming…' : 'Resume existing case'}</button>
  </form>;
}
